import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useAppDispatch, useAppSelector } from 'store';
import { selectUser } from 'store/general/general-slice-selectors';
import OrganizationRole from 'models/enums/organization-roles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SphereNavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FleetFiltersBar, { RobotModel, RobotScope } from './FleetFiltersBar';
import FleetContent from './FleetContent';
import FleetLayout from './FleetLayout';
import OrbitDashboard from 'components/fleet-management/OrbitDashboard';
import { FilterKeys } from '@common/enums/query-params/filters';
import {
  createNewFleetDashboard,
  deleteDashboardById,
  getFleetDashboards,
  getFleetDataSets,
  saveFleetDashboard,
} from 'store/fleet-management/fleet-management-actions';
import {
  builtInPageFor,
  isSavedViewType,
  pageForViewType,
  resolveGeneralPage,
} from 'models/fleet-management/FleetDashboard';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import { AnalyticsEvent } from 'services/analytics-events';
import { describeFilters } from 'services/analytics-filters';
import {
  selectCurrentDashboard,
  selectFleetDashboardsData,
  selectFleetDashboardsRestState,
  selectFleetDataSetsRestState,
} from 'store/fleet-management/fleet-management-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { useTranslation } from 'react-i18next';
import { EditIcon, SaveIcon } from '@common/utils/icons/icons';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  patchDashboardsRestState,
  setCurrentDashboardById,
} from 'store/fleet-management/fleet-management-slice';
import {
  DEFAULT_DATE_PERIOD,
  DatePeriod,
  FleetDashboardFilters,
  fleetDataFiltersToDashboardFilters,
  dashboardFiltersToFleetDataFilters,
  getDefaultFilters,
  getPresetRange,
  isDatePreset,
} from 'models/fleet-management/FleetDataFilter';
import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import TextInput from '@common/components/Inputs/TextInput';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { PageOption } from 'models/enums/fleet-management';
import { FLEET_SECTION_GAP } from 'utils/fleet-dashboard-theme';

/**
 * The window a link asks for. Dates in the link are what was asked for and always win; the
 * address only carries them for a custom window. A preset is written without dates, so a bookmark
 * of one is worked out again from today. With neither, the default preset holds.
 */
const windowFromQueryParams = (queryParams: URLSearchParams) => {
  const period = queryParams.get(FilterKeys.DatePeriod);
  const start = queryParams.get(FilterKeys.StartDate);
  const end = queryParams.get(FilterKeys.EndDate);

  if (start && end) {
    // Dates that are exactly a preset's window today still read as that preset.
    const preset = isDatePreset(period) ? getPresetRange(period) : null;
    if (isDatePreset(period) && preset?.start === start && preset.end === end) {
      return { period, ...preset };
    }
    return { period: DatePeriod.CUSTOM, start, end };
  }
  if (isDatePreset(period)) return { period, ...getPresetRange(period) };
  return { period: DEFAULT_DATE_PERIOD, ...getPresetRange(DEFAULT_DATE_PERIOD) };
};

const filtersFromQueryParams = (queryParams: URLSearchParams): FleetDashboardFilters => {
  return {
    model: Object.values(RobotModel).includes(queryParams.get(FilterKeys.RobotModel) as RobotModel)
      ? (queryParams.get(FilterKeys.RobotModel) as RobotModel)
      : RobotModel.ALL,
    robotScope:
      queryParams.get(FilterKeys.RobotScope) === RobotScope.ALL
        ? RobotScope.ALL
        : RobotScope.CUSTOMER,
    ...windowFromQueryParams(queryParams),
    dealers: queryParams.getAll(FilterKeys.Dealer),
    dealersCustomers: queryParams.getAll(FilterKeys.Customer),
    tags: queryParams.getAll(FilterKeys.Tag),
    countries: queryParams.getAll(FilterKeys.Country),
  };
};

const FleetDashboard: React.FC<{ datasetPage?: boolean }> = ({ datasetPage }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => selectUser(state));
  const dashboardsData = useAppSelector((state) => selectFleetDashboardsData(state));
  const currentDashboard = useAppSelector((state) => selectCurrentDashboard(state));
  const fleetDataSetRest = useAppSelector((state) => selectFleetDataSetsRestState(state));
  const fleetDashboardsRest = useAppSelector((state) => selectFleetDashboardsRestState(state));

  const isManufacturer = currentUser.data.organization.role === OrganizationRole.MANUFACTURER;
  const isDistributorOrHigher = [
    OrganizationRole.IMPORTER,
    OrganizationRole.WHOLESALER,
    OrganizationRole.DISTRIBUTOR,
  ].includes(currentUser.data.organization.role);
  const canSeeDealerOverview = [
    OrganizationRole.MANUFACTURER,
    OrganizationRole.WHOLESALER,
    OrganizationRole.IMPORTER,
  ].includes(currentUser.data.organization.role);

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isDashboardDialogOpen, setIsDashboardDialogOpen] = useState<boolean>(false);
  const [isEditingFilters, setIsEditingFilters] = useState(false);

  const [queryParams, setQueryParams] = useSearchParams();
  const queryDashboardId = queryParams.get(FilterKeys.FleetDashboard);
  const queryDashboardView = queryParams.get(FilterKeys.DashboardView);
  const queryDatasetId = queryParams.get(FilterKeys.FleetDataset);

  // The built-in pages share one set of filters, so moving between them keeps the selection and
  // reuses every dataset they have in common. It starts from the address so a reload or a shared
  // link opens exactly that selection, and the page fetches once with it.
  const [generalFilters, setGeneralFilters] = useState<FleetDashboardFilters>(() =>
    queryDashboardId ? getDefaultFilters() : filtersFromQueryParams(queryParams),
  );
  // A saved dashboard opens with its own filters and leaves the shared ones untouched.
  const [savedFilters, setSavedFilters] = useState<{
    dashboardId: string;
    filters: FleetDashboardFilters;
  } | null>(null);

  const filters = useMemo(() => {
    if (!queryDashboardId) return generalFilters;
    return savedFilters?.dashboardId === queryDashboardId
      ? savedFilters.filters
      : getDefaultFilters();
  }, [queryDashboardId, generalFilters, savedFilters]);
  const filtersReady = !queryDashboardId || savedFilters?.dashboardId === queryDashboardId;

  const setFilters = useCallback<React.Dispatch<React.SetStateAction<FleetDashboardFilters>>>(
    (next) => {
      if (!queryDashboardId) {
        setGeneralFilters(next);
        return;
      }
      setSavedFilters((previous) => {
        const current =
          previous?.dashboardId === queryDashboardId ? previous.filters : getDefaultFilters();
        return {
          dashboardId: queryDashboardId,
          filters: typeof next === 'function' ? next(current) : next,
        };
      });
    },
    [queryDashboardId],
  );

  // The generic dashboard shown when no saved dashboard is selected is driven by the navigation bar
  // through the query parameters.
  const selectedGeneralPage: PageOption = resolveGeneralPage(
    queryDashboardView,
    canSeeDealerOverview,
  );

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: dashboardsData.rest.postError,
      patchError: dashboardsData.rest.patchError,
      resetPostHandler: () =>
        dispatch(patchDashboardsRestState({ postStatus: HttpFetchStatus.Idle, postError: null })),
      resetPatchHandler: () =>
        dispatch(patchDashboardsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  useEffect(() => {
    if (!isManufacturer) {
      const redirectPage = !isDistributorOrHigher
        ? SphereNavigationRoutes.Dashboard
        : datasetPage
          ? SphereNavigationRoutes.Insights
          : null;
      if (redirectPage) {
        const id = setTimeout(() => navigate(redirectPage, { replace: true }), 0);
        return () => clearTimeout(id);
      }
    }
  }, [
    navigate,
    currentUser.data.organization.role,
    datasetPage,
    isManufacturer,
    isDistributorOrHigher,
  ]);

  useEffect(() => {
    if (dashboardsData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getFleetDashboards());
    }
  }, [dispatch, dashboardsData.rest.getStatus]);

  useEffect(() => {
    if (fleetDataSetRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getFleetDataSets());
    }
  }, [dispatch, fleetDataSetRest.getStatus]);

  useEffect(() => {
    dispatch(setCurrentDashboardById(queryDashboardId ?? ''));
  }, [dispatch, queryDashboardId]);

  useEffect(() => {
    // Leaving a saved dashboard drops unsaved edits, so it reopens with what it stores.
    if (currentDashboard == null) {
      setSavedFilters((previous) => (previous == null ? previous : null));
      return;
    }
    if (savedFilters?.dashboardId === currentDashboard.id) return;

    setSavedFilters({
      dashboardId: currentDashboard.id,
      filters: fleetDataFiltersToDashboardFilters(currentDashboard.filters || []),
    });
  }, [currentDashboard, savedFilters?.dashboardId]);

  useEffect(() => {
    setIsEditingFilters(false);
  }, [currentDashboard]);

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    currentDashboard?.name ?? '',
    (_) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const {
    value: description,
    validationResult: descriptionValidationResult,
    valueChangeHandler: descriptionChangeHandler,
  } = useValidatedInputEffect(
    currentDashboard?.description ?? '',
    (_) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('description'),
    removeServerError,
    resetToggle,
  );

  const activePage: PageOption = useMemo(() => {
    if (datasetPage) return PageOption.Orbit;

    if (currentDashboard) {
      return pageForViewType(currentDashboard.viewType);
    }

    return selectedGeneralPage;
  }, [datasetPage, currentDashboard, selectedGeneralPage]);

  // The generic dashboards are named after the view they show; a saved dashboard by its own name.
  const activeBuiltInPage = builtInPageFor(activePage);
  const pageTitle = currentDashboard
    ? currentDashboard.name
    : activeBuiltInPage
      ? t(activeBuiltInPage.titleKey)
      : '';

  useEffect(() => {
    trackEvent(AnalyticsEvent.InsightsViewed, {
      view_type: activePage,
      is_dataset_page: datasetPage === true,
      dashboard_id: queryDashboardId,
      dataset_id: queryDatasetId,
    });
  }, [activePage, datasetPage, queryDashboardId, queryDatasetId]);

  // Must stay referentially stable: FleetFiltersBar lists onChange in effect dependencies.
  // Functional updates come from the bar's own effects (pruning stale selections), not the user.
  const filtersChangeHandler = useCallback(
    (update: React.SetStateAction<FleetDashboardFilters>) => {
      if (typeof update !== 'function') {
        trackEvent(AnalyticsEvent.FiltersChanged, {
          ...describeFilters(update, 'start', 'end'),
          source_page: getCurrentPageName(),
        });
      }
      setFilters(update);
    },
    [setFilters],
  );

  useEffect(() => {
    if (queryDashboardId || activePage === PageOption.Orbit) return;

    setQueryParams(
      (prevState) => {
        prevState.delete(FilterKeys.FleetDataset);
        prevState.set(FilterKeys.DashboardView, activePage);
        prevState.set(FilterKeys.RobotModel, generalFilters.model);
        prevState.set(FilterKeys.RobotScope, generalFilters.robotScope);
        prevState.set(FilterKeys.DatePeriod, generalFilters.period);
        // A preset is relative to today, so only a custom window pins its dates in the address.
        if (generalFilters.period === DatePeriod.CUSTOM) {
          prevState.set(FilterKeys.StartDate, generalFilters.start);
          prevState.set(FilterKeys.EndDate, generalFilters.end);
        } else {
          prevState.delete(FilterKeys.StartDate);
          prevState.delete(FilterKeys.EndDate);
        }

        prevState.delete(FilterKeys.Dealer);
        generalFilters.dealers.forEach((d) => prevState.append(FilterKeys.Dealer, d));

        prevState.delete(FilterKeys.Customer);
        generalFilters.dealersCustomers.forEach((d) => prevState.append(FilterKeys.Customer, d));

        prevState.delete(FilterKeys.Tag);
        generalFilters.tags.forEach((tag) => prevState.append(FilterKeys.Tag, tag));

        prevState.delete(FilterKeys.Country);
        generalFilters.countries.forEach((country) =>
          prevState.append(FilterKeys.Country, country),
        );

        return prevState;
      },
      { replace: true },
    );
  }, [generalFilters, queryDashboardId, activePage, setQueryParams]);

  const resetFiltersHandler = () => {
    setFilters(getDefaultFilters());
  };

  const saveFiltersHandler = async () => {
    try {
      if (currentDashboard) {
        await dispatch(
          saveFleetDashboard({
            ...currentDashboard,
            name,
            description,
            filters: dashboardFiltersToFleetDataFilters(filters),
          }),
        ).unwrap();
      } else if (activeBuiltInPage) {
        const dashboard = await dispatch(
          createNewFleetDashboard({
            name,
            description,
            icon: activeBuiltInPage.icon,
            isDefault: false,
            widgets: [],
            viewType: activeBuiltInPage.viewType,
            filters: dashboardFiltersToFleetDataFilters(filters),
          }),
        ).unwrap();

        navigate(`${SphereNavigationRoutes.Insights}?${FilterKeys.FleetDashboard}=${dashboard.id}`);
      }
      setIsDashboardDialogOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const resetDashboardRestStateHandler = () => {
    dispatch(
      patchDashboardsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  const deleteClickHandler = () => {
    setIsConfirmDeleteOpen(true);
  };

  const abortDeleteHandler = () => {
    setIsConfirmDeleteOpen(false);
  };

  const confirmDeleteHandler = () => {
    setIsConfirmDeleteOpen(false);
    if (currentDashboard) {
      dispatch(deleteDashboardById(currentDashboard.id));
      setQueryParams((prevState) => {
        // With no view named, the default page opens.
        prevState.delete(FilterKeys.DashboardView);
        prevState.delete(FilterKeys.FleetDashboard);
        return prevState;
      });
    }
  };

  const onSaveFilterHandler = () => {
    if (isEditingFilters) {
      saveFiltersHandler();
    } else {
      setIsDashboardDialogOpen(true);
    }
  };

  const openDashboardDialogHandler = () => {
    setIsDashboardDialogOpen(true);
  };

  const abortDashboardDialogHandler = () => {
    setIsDashboardDialogOpen(false);
  };

  return (
    <React.Fragment>
      <FleetLayout onEditDashboard={openDashboardDialogHandler}>
        {isManufacturer && (datasetPage || activePage === PageOption.Orbit) ? (
          <OrbitDashboard datasetPage={datasetPage} />
        ) : (
          <Stack
            gap={FLEET_SECTION_GAP}
            px={{ xs: 2, md: 3, lg: 4 }}
            py={{ xs: 2, md: 3 }}
            width="100%"
            maxWidth="xl"
            mx="auto"
          >
            {pageTitle && (
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    {pageTitle}
                  </Typography>
                  {isManufacturer &&
                    currentDashboard != null &&
                    isSavedViewType(currentDashboard.viewType) && (
                      <Tooltip title={t('fleet-management-title-dashboard-configuration')}>
                        <IconButton
                          size="small"
                          onClick={openDashboardDialogHandler}
                          aria-label={t('fleet-management-title-dashboard-configuration')}
                          data-testid="fleet-edit-dashboard-button"
                          sx={{ color: 'text.secondary' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
                {currentDashboard?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {currentDashboard.description}
                  </Typography>
                )}
              </Stack>
            )}
            <FleetFiltersBar
              filters={filters}
              onChange={filtersChangeHandler}
              onReset={resetFiltersHandler}
              onSave={onSaveFilterHandler}
              onDelete={deleteClickHandler}
              isSavedDashboard={Boolean(queryDashboardId)}
              canSave={isManufacturer}
              isEditing={isEditingFilters}
              onEditingChange={setIsEditingFilters}
            />
            <FleetContent page={activePage} filters={filters} filtersReady={filtersReady} />
          </Stack>
        )}
      </FleetLayout>

      <DialogWrapperWithClose
        onClose={abortDashboardDialogHandler}
        open={isDashboardDialogOpen}
        title={t('fleet-management-title-dashboard-configuration')}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <Stack mt={2} spacing={2}>
            <TextInput
              value={name}
              onChange={nameChangeHandler}
              validationResult={nameValidationResult}
              label={t('input-label-name')}
            />
            <TextInput
              value={description}
              onChange={descriptionChangeHandler}
              validationResult={descriptionValidationResult}
              label={t('input-label-description')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SaveIcon />}
            disabled={!name || !description}
            onClick={() => saveFiltersHandler()}
          >
            {t('button-save')}
          </Button>
        </DialogActions>
      </DialogWrapperWithClose>
      <ConfirmAbortDialog
        isOpen={isConfirmDeleteOpen}
        title={t('title-delete-dashboard')}
        description={t('info-delete-dashboard')}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={confirmDeleteHandler}
        onAbort={abortDeleteHandler}
      />
      <CustomSnackbar
        open={fleetDashboardsRest.patchStatus === HttpFetchStatus.Success}
        title={t('success-saving-dashboard')}
        onClose={resetDashboardRestStateHandler}
      />
      <CustomSnackbar
        open={fleetDashboardsRest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-dashboard')}
        onClose={resetDashboardRestStateHandler}
      />
      <CustomSnackbar
        open={fleetDashboardsRest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-dashboard')}
        onClose={resetDashboardRestStateHandler}
      />
    </React.Fragment>
  );
};

export default FleetDashboard;
